import { createSlice } from "@reduxjs/toolkit";
import {
  getUtilityReadingsThunk,
  getUtilityReadingByIdThunk,
  inputUtilityReadingThunk,
  inputUtilityReadingBatchThunk,
  previewUtilityInvoiceThunk,
  generateUtilityInvoiceThunk,
  calculateElectricityPreviewThunk,
  calculateWaterPreviewThunk,
  verifyUtilityReadingThunk,
} from "state/action/utility_reading";

const asyncState = (overrides = {}) => ({
  data: null,
  isPending: false,
  isFulfilled: false,
  isRejected: false,
  err: null,
  ...overrides,
});

const defaultElectricityParams = {
  kva_value: "",
  price_per_kwh: "",
  pju_rate_percent: "3",
  vat_non_adm_rate_percent: "11",
  admin_fee_rate_percent: "10",
};

const initialState = {
  getReadings: asyncState(),
  getReadingById: asyncState(),
  inputReading: asyncState(),
  inputBatch: asyncState(),
  previewInvoice: asyncState(),
  generateInvoice: asyncState(),
  calculatePreview: asyncState(),
  calculateWaterPreview: asyncState(),
  verifyReading: asyncState(),

  readingForm: {
    property: "",
    building: "",
    floor: "",
    unit_id: "",
    utility_type: "",
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    reading_start: "",
    reading_end: "",
    unit_price: "",
    notes: "",
    read_date: "",
    electricity_mode: "",
    electricityParams: { ...defaultElectricityParams },
    evidence: { file: "", url: "" },
  },

  batchForm: {
    period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(),
    readings: [],
  },

  filter: {
    unit_id: "",
    utility_type: "",
    period_month: "",
    period_year: new Date().getFullYear(),
    status: "",
  },

  previewData: null,
  selectedReading: {},
};

function addAsyncCases(builder, thunk, stateKey) {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey] = asyncState({ isPending: true });
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey] = asyncState({ isFulfilled: true, data: action.payload });
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey] = asyncState({
        isRejected: true,
        err: action.error.message,
      });
    });
}

const utilityReadingSlice = createSlice({
  name: "utilityReading",
  initialState,
  reducers: {
    setReadingForm: (state, action) => {
      state.readingForm = { ...state.readingForm, ...action.payload };
    },
    setElectricityParams: (state, action) => {
      state.readingForm.electricityParams = {
        ...state.readingForm.electricityParams,
        ...action.payload,
      };
    },
    setSelectedReading: (state, action) => {
      state.selectedReading = action.payload;
    },
    resetReadingForm: (state) => {
      state.readingForm = initialState.readingForm;
      state.calculatePreview = asyncState();
      state.calculateWaterPreview = asyncState();
    },
    setBatchForm: (state, action) => {
      state.batchForm = { ...state.batchForm, ...action.payload };
    },
    resetBatchForm: (state) => {
      state.batchForm = initialState.batchForm;
    },
    setFilter: (state, action) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    resetFilter: (state) => {
      state.filter = initialState.filter;
    },
    setPreviewData: (state, action) => {
      state.previewData = action.payload;
    },
    clearPreviewData: (state) => {
      state.previewData = null;
    },
    clearGenerateInvoice: (state) => {
      state.generateInvoice = asyncState();
    },
    clearCalculatePreview: (state) => {
      state.calculatePreview = asyncState();
    },
    clearCalculateWaterPreview: (state) => {
      state.calculateWaterPreview = asyncState();
    },
  },
  extraReducers(builder) {
    addAsyncCases(builder, getUtilityReadingsThunk, "getReadings");
    addAsyncCases(builder, getUtilityReadingByIdThunk, "getReadingById");
    addAsyncCases(builder, inputUtilityReadingThunk, "inputReading");
    addAsyncCases(builder, inputUtilityReadingBatchThunk, "inputBatch");
    addAsyncCases(builder, previewUtilityInvoiceThunk, "previewInvoice");
    addAsyncCases(builder, generateUtilityInvoiceThunk, "generateInvoice");
    addAsyncCases(
      builder,
      calculateElectricityPreviewThunk,
      "calculatePreview",
    );
    addAsyncCases(builder, calculateWaterPreviewThunk, "calculateWaterPreview");
    addAsyncCases(builder, verifyUtilityReadingThunk, "verifyReading");
  },
});

export const utilityReadingAction = {
  ...utilityReadingSlice.actions,
  getUtilityReadingsThunk,
  getUtilityReadingByIdThunk,
  inputUtilityReadingThunk,
  inputUtilityReadingBatchThunk,
  previewUtilityInvoiceThunk,
  generateUtilityInvoiceThunk,
  calculateElectricityPreviewThunk,
  calculateWaterPreviewThunk,
  verifyUtilityReadingThunk,
};

export function selectUtilityReading(state) {
  return state?.utilityReading;
}

export default utilityReadingSlice.reducer;
